import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Check, Clock, AlertCircle, Video, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import api, { mensajeDeError, pesos } from '../lib/api';
import { toast } from 'sonner';

/**
 * Resultado del pago.
 *
 * El estado NO se toma de los parámetros que devuelve la pasarela en la URL:
 * se le pregunta al servidor, que a su vez lo verifica contra la pasarela.
 * Confiar en la URL permitiría marcarse como pagado a mano.
 *
 * Mercado Pago avisa por su cuenta y ese aviso puede tardar unos segundos, así
 * que se reintenta unas cuantas veces antes de darse por vencido.
 */
const PagoResultadoPage = () => {
  const [searchParams] = useSearchParams();
  const reservaId = searchParams.get('reserva');

  // La pasarela también devuelve aquí tras comprar un bono, con ?bono=…
  // Antes esta página solo miraba ?reserva=, así que quien pagaba un pack de
  // ocho sesiones volvía a una pantalla que decía "No sabemos qué reserva
  // mostrar", sin confirmación de haber pagado.
  const bonoId = searchParams.get('bono');
  const esBono = Boolean(bonoId && !reservaId);

  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [reintentando, setReintentando] = useState(false);
  const intentos = useRef(0);

  const consultar = useCallback(async () => {
    if (!reservaId && !bonoId) {
      setCargando(false);
      return null;
    }

    try {
      const { data } = esBono
        ? await api.get('/pagos/bono', { params: { bono: bonoId } })
        : await api.get('/pagos/estado', { params: { reserva: reservaId } });
      setEstado(data);
      return data;
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos consultar el estado de tu pago'));
      return null;
    } finally {
      setCargando(false);
    }
  }, [reservaId, bonoId, esBono]);

  useEffect(() => {
    let vigente = true;
    let temporizador;

    const listo = (data) => (esBono ? data.status === 'active' : data.payment_status === 'paid');

    const revisar = async () => {
      const data = await consultar();
      if (!vigente) return;

      // Si aún no está confirmado, se vuelve a preguntar: el aviso de la
      // pasarela puede demorar. Se corta a los 10 intentos para no quedar
      // en bucle.
      if (data && !listo(data) && intentos.current < 10) {
        intentos.current += 1;
        temporizador = setTimeout(revisar, 3000);
      }
    };

    revisar();

    return () => {
      vigente = false;
      clearTimeout(temporizador);
    };
  }, [consultar, esBono]);

  const reintentarPago = async () => {
    setReintentando(true);
    try {
      const { data } = await api.post(`/reservas/${reservaId}/pagar`);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      await consultar();
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos reiniciar el pago'));
    } finally {
      setReintentando(false);
    }
  };

  if (!reservaId && !bonoId) {
    return (
      <div className="py-24 text-center px-4">
        <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
          No sabemos qué pago mostrar
        </h1>
        <Link to="/mis-horas">
          <Button variant="outline" className="rounded-full">Ir a mis horas</Button>
        </Link>
      </div>
    );
  }

  if (cargando) {
    return (
      <div className="py-24 text-center text-slate-500">
        <Clock className="w-8 h-8 mx-auto mb-4 animate-pulse" />
        Confirmando tu pago…
      </div>
    );
  }

  const pagado = esBono ? estado?.status === 'active' : estado?.payment_status === 'paid';
  const fallido = esBono ? estado?.status === 'failed' : estado?.payment_status === 'failed';
  const fecha = estado?.scheduled_date ? parseISO(estado.scheduled_date) : null;

  if (esBono && pagado) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-lg">
          <Card className="border-slate-200">
            <CardContent className="pt-10 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-teal-700" />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                Tu bono está activo
              </h1>
              <p className="text-slate-600 mb-1">
                {estado.total_sessions} sesiones con {estado.psychologist_name}
              </p>
              <p className="text-sm text-slate-500 mb-8">
                Pagaste {pesos(estado.amount_paid)} · te quedan {estado.remaining}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/agendar?profesional=${estado.psychologist_id}`}>
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 w-full">
                    Agendar mi primera sesión
                  </Button>
                </Link>
                <Link to="/mis-horas">
                  <Button variant="outline" className="rounded-full px-8 w-full">
                    Ver mis bonos
                  </Button>
                </Link>
              </div>

              {estado.expires_at && (
                <p className="text-xs text-slate-500 mt-6">
                  Puedes usarlo hasta el{' '}
                  <span className="first-letter:uppercase">
                    {format(parseISO(estado.expires_at), "d 'de' MMMM 'de' yyyy", { locale: es })}
                  </span>
                  .
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg">
        <Card className="border-slate-200">
          <CardContent className="pt-10 pb-8 text-center">
            {pagado && (
              <>
                <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
                  <Check className="w-8 h-8 text-teal-700" />
                </div>
                <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                  ¡Tu sesión está confirmada!
                </h1>
                {fecha && (
                  <p className="text-slate-600 mb-1">
                    {format(fecha, "EEEE d 'de' MMMM 'a las' HH:mm", { locale: es })} hrs
                  </p>
                )}
                <p className="text-sm text-slate-500 mb-8">
                  Pagaste {pesos(estado.amount)}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/mis-horas">
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 w-full">
                      Ver mis horas
                    </Button>
                  </Link>
                  {estado.meeting_url && (
                    <a href={estado.meeting_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="rounded-full px-8 w-full">
                        <Video className="w-4 h-4 mr-2" />
                        Ver la sala
                      </Button>
                    </a>
                  )}
                </div>

                <p className="text-xs text-slate-500 mt-6">
                  El enlace de la sesión se activa 10 minutos antes de la hora.
                </p>
              </>
            )}

            {!pagado && !fallido && (
              <>
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
                  <Clock className="w-8 h-8 text-amber-700" />
                </div>
                <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                  Estamos confirmando tu pago
                </h1>
                <p className="text-slate-600 mb-8 max-w-sm mx-auto">
                  A veces demora un momento en llegarnos la confirmación.{' '}
                  {esBono
                    ? 'En cuanto llegue, tus sesiones quedan disponibles.'
                    : 'Tu horario está guardado mientras tanto.'}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={consultar}
                    variant="outline"
                    className="rounded-full px-8"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Volver a revisar
                  </Button>
                  <Link to="/mis-horas">
                    <Button variant="ghost" className="rounded-full px-8 w-full">
                      Ir a mis horas
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {fallido && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
                  <AlertCircle className="w-8 h-8 text-red-700" />
                </div>
                <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                  El pago no se completó
                </h1>
                <p className="text-slate-600 mb-8 max-w-sm mx-auto">
                  No se te cobró nada.{' '}
                  {esBono
                    ? 'Puedes volver a comprar el bono cuando quieras.'
                    : 'Puedes intentarlo otra vez; tu horario sigue reservado por un rato más.'}
                </p>

                {esBono ? (
                  <Link to="/bonos">
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
                      Volver a los bonos
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={reintentarPago}
                    disabled={reintentando}
                    className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8"
                  >
                    {reintentando ? 'Preparando…' : 'Intentar el pago de nuevo'}
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PagoResultadoPage;
